import { LibraryButton, libraryNote, type LibraryRef } from './LibraryPicker';
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { ArrowUp, Bot, Brain, ChevronDown, FileText, Globe, Image as ImageIcon, Layers, MessageCircle, Paperclip, Square, Telescope, X, AlertCircle } from 'lucide-react';
import { api, uploadFile } from '../../lib/api';
import { bytes, cx } from '../../lib/format';
import { useDraft } from '../../hooks/useDraft';
import type { FileRecord, ModelInfo, RunMode } from '../../types/api';
import { IconButton, Spinner } from '../../components/ui';
import { useAccount } from '../plans/useAccount';
import { showUpgradePrompt } from '../plans/UpgradePrompt';

export interface SendPayload { text: string; attachmentIds: string[]; mode: RunMode; model: string; thinking: boolean; webSearch: boolean }
interface Pending { key: string; file: File; progress: number; record?: FileRecord; error?: string; preview?: string }

const MODES: { id: RunMode; label: string; hint: string; icon: React.ReactElement }[] = [
  { id: 'chat', label: 'Chat', hint: 'Conversation with tools and artifacts', icon: <MessageCircle size={15} /> },
  { id: 'agent', label: 'Agent', hint: 'Autonomous multi-step work with verification', icon: <Bot size={15} /> },
  { id: 'research', label: 'Research', hint: 'Deep web research with a cited report', icon: <Telescope size={15} /> },
];

export function useModels() {
  return useQuery({ queryKey: ['models'], queryFn: () => api<{ models: ModelInfo[]; default: string; configured: boolean; managedAgents: boolean }>('/api/models'), staleTime: Infinity });
}

export function Composer({ draftKey, onSend, running, onStop, conversationId, projectId, autoFocus, placeholder }: {
  draftKey: string; onSend: (p: SendPayload) => Promise<void> | void; running: boolean; onStop?: () => void;
  conversationId?: string; projectId?: string | null; autoFocus?: boolean; placeholder?: string;
}) {
  const [text, setText] = useDraft(draftKey);
  const [pending, setPending] = useState<Pending[]>([]);
  const [libRefs, setLibRefs] = useState<LibraryRef[]>([]);
  const [dragging, setDragging] = useState(false);
  const [mode, setMode] = useState<RunMode>(() => (localStorage.getItem('lumnos.mode') as RunMode) || 'chat');
  const [model, setModel] = useState<string>(() => localStorage.getItem('lumnos.model') ?? '');
  const [thinking, setThinking] = useState(() => localStorage.getItem('lumnos.thinking') === '1');
  const [webSearch, setWebSearch] = useState(() => localStorage.getItem('lumnos.web') !== '0');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const models = useModels();
  const account = useAccount();
  const activeModel = model || models.data?.default || '';
  const managedReasoning = mode === 'agent' && !!models.data?.managedAgents;
  const thinkingAlwaysOn = models.data?.models.find((m) => m.id === activeModel)?.supportsThinkingOff === false;
  const thinkingControlled = !managedReasoning && !thinkingAlwaysOn;

  useEffect(() => { const el = ta.current; if (!el) return; el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 320)}px`; }, [text]);
  useEffect(() => { if (autoFocus) ta.current?.focus(); }, [autoFocus, draftKey]);
  const persist = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

  const addFiles = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      setPending((p) => [...p, { key, file, progress: 0, preview }]);
      uploadFile(file, { conversationId, projectId: undefined }, (progress) => setPending((p) => p.map((x) => (x.key === key ? { ...x, progress } : x))))
        .then((record) => setPending((p) => p.map((x) => (x.key === key ? { ...x, record, progress: 1 } : x))))
        .catch((e) => setPending((p) => p.map((x) => (x.key === key ? { ...x, error: e.message } : x))));
    }
  };

  const uploading = pending.some((p) => !p.record && !p.error);
  const canSend = !running && !sending && !uploading && (text.trim().length > 0 || pending.some((p) => p.record) || libRefs.length > 0);

  const send = async () => {
    if (!canSend || sendingRef.current) return;
    if (account.data?.usage.remaining !== null && account.data?.usage.remaining !== undefined && account.data.usage.remaining <= 0) {
      showUpgradePrompt();
      return;
    }
    // React state updates don't close the same-tick Enter/click race.
    sendingRef.current = true;
    setSending(true);
    try {
      await onSend({ text: (text.trim() || (libRefs.length ? 'Use these from my library.' : '')) + libraryNote(libRefs), attachmentIds: pending.filter((p) => p.record).map((p) => p.record!.id), mode, model: activeModel, thinking, webSearch });
      setText('');
      pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPending([]);
      setLibRefs([]);
    } finally { sendingRef.current = false; setSending(false); }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); }
    if (e.key === 'Escape' && running) onStop?.();
  };
  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length) { e.preventDefault(); addFiles(files); }
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); };

  const modeInfo = MODES.find((m) => m.id === mode)!;
  return (
    <div className={cx('composer', dragging && 'is-dragging')} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}>
      {libRefs.length > 0 && (
        <div className="composer__files">
          {libRefs.map((r) => (
            <div key={r.key} className="attach attach--lib">
              <span className="attach__icon"><Layers size={16} /></span>
              <div className="attach__meta"><span className="attach__name">{r.label}</span><span className="attach__sub">{r.detail} · from your library</span></div>
              <IconButton size="sm" label="Remove" onClick={() => setLibRefs((x) => x.filter((y) => y.key !== r.key))}><X size={14} /></IconButton>
            </div>
          ))}
        </div>
      )}
      {pending.length > 0 && (
        <div className="composer__files">
          {pending.map((p) => (
            <div key={p.key} className={cx('attach', p.error && 'attach--error')}>
              {p.preview ? <img src={p.preview} alt="" /> : <span className="attach__icon">{p.error ? <AlertCircle size={16} /> : <FileText size={16} />}</span>}
              <div className="attach__meta">
                <span className="attach__name">{p.file.name}</span>
                <span className="attach__sub">{p.error ?? (p.record ? bytes(p.file.size) : (p.progress >= 1 ? 'Processing upload…' : `Uploading ${Math.round(p.progress * 100)}%`))}</span>
              </div>
              {!p.record && !p.error ? <Spinner size={14} /> : null}
              <IconButton size="sm" label={`Remove ${p.file.name}`} onClick={() => setPending((x) => x.filter((y) => y.key !== p.key))}><X size={13} /></IconButton>
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={ta}
        className="composer__input"
        rows={1}
        value={text}
        placeholder={placeholder ?? (mode === 'research' ? 'What should I research?' : mode === 'agent' ? 'Describe the task to complete…' : 'Message Lumnos…')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        onPaste={onPaste}
        aria-label="Message"
      />
      <div className="composer__bar">
        <div className="composer__tools">
          <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
          <IconButton label="Attach files" onClick={() => fileInput.current?.click()}><Paperclip size={17} /></IconButton>
          <LibraryButton selected={libRefs} onChange={setLibRefs} />
          <Menu.Root>
            <Menu.Trigger asChild><button className="chip" aria-label="Mode">{modeInfo.icon}{modeInfo.label}<ChevronDown size={13} /></button></Menu.Trigger>
            <Menu.Portal>
              <Menu.Content className="menu" side="top" align="start" sideOffset={6}>
                {MODES.map((m) => (
                  <Menu.Item key={m.id} className={cx('menu__item menu__item--rich', m.id === mode && 'is-selected')} onSelect={() => { setMode(m.id); persist('lumnos.mode', m.id); }}>
                    {m.icon}<div><div>{m.label}</div><small>{m.hint}{m.id === 'agent' && models.data?.managedAgents ? ' · Managed Agents' : ''}</small></div>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Portal>
          </Menu.Root>
          <button className={cx('chip', webSearch && 'is-on')} aria-pressed={webSearch} onClick={() => { setWebSearch(!webSearch); persist('lumnos.web', webSearch ? '0' : '1'); }} title="Web search">
            <Globe size={14} /><span className="chip__label">Search</span>
          </button>
          <button className={cx('chip', (thinking || !thinkingControlled) && 'is-on')} aria-pressed={thinking || !thinkingControlled} disabled={!thinkingControlled} onClick={() => { setThinking(!thinking); persist('lumnos.thinking', thinking ? '0' : '1'); }} title={managedReasoning ? 'Agent mode manages its own reasoning.' : thinkingAlwaysOn ? 'This model always uses reasoning. Think cannot be switched off.' : 'Extended thinking'}>
            <Brain size={14} /><span className="chip__label">{managedReasoning ? 'Auto thinking' : thinkingAlwaysOn ? 'Think · always on' : 'Think'}</span>
          </button>
        </div>
        <div className="composer__send">
          <Menu.Root>
            <Menu.Trigger asChild><button className="chip chip--plain" aria-label="Model">{models.data?.models.find((m) => m.id === activeModel)?.label ?? 'Model'}<ChevronDown size={13} /></button></Menu.Trigger>
            <Menu.Portal>
              <Menu.Content className="menu" side="top" align="end" sideOffset={6}>
                {models.data?.models.map((m) => (
                  <Menu.Item key={m.id} className={cx('menu__item menu__item--rich', m.id === activeModel && 'is-selected')} onSelect={() => { setModel(m.id); persist('lumnos.model', m.id); }}>
                    <div><div>{m.label}</div><small>{m.description}</small></div>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Portal>
          </Menu.Root>
          {running ? (
            <button className="send-btn send-btn--stop" onClick={onStop} aria-label="Stop generating"><Square size={14} fill="currentColor" /></button>
          ) : (
            <button className="send-btn" onClick={() => void send()} disabled={!canSend} aria-label="Send message">{sending ? <Spinner size={16} /> : <ArrowUp size={18} />}</button>
          )}
        </div>
      </div>
      {dragging && <div className="composer__drop"><ImageIcon size={18} /> Drop files to attach</div>}
      {models.data && !models.data.configured && <div className="composer__warn">AI isn’t set up on the server yet, so replies will fail until it is.</div>}
    </div>
  );
}
